import * as ToastPrimitive from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useState, type CSSProperties, type ReactNode } from 'react';
import type { StatusTone } from '../../tokens/tokens.ts';

export function Badge({ tone = 'neutral', children }: { tone?: StatusTone; children: ReactNode }) {
  return <span className={`bb-badge bb-tone--${tone}`}>{children}</span>;
}

/** Status with a dot and text (never colour alone). */
export function StatusPill({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return (
    <span className={`bb-pill bb-tone--${tone}`}>
      <span className="bb-pill__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

export function Skeleton({ width = '100%', height = 14, label = 'Loading' }: { width?: CSSProperties['width']; height?: CSSProperties['height']; label?: string }) {
  return <span className="bb-skeleton" style={{ width, height }} role="status" aria-label={label} aria-busy="true" />;
}

export interface ToastMessage {
  title: string;
  description?: string;
  tone?: Exclude<StatusTone, 'prod' | 'dev'>;
}

const ToastContext = createContext<((message: ToastMessage) => void) | null>(null);

/** Hosts toasts for the app; `useToast()` shows one. Errors that need action belong inline, not in a toast. */
export function ToastProvider({ children, duration = 5000 }: { children: ReactNode; duration?: number }) {
  const [toasts, setToasts] = useState<Array<ToastMessage & { id: number }>>([]);
  const notify = useCallback((message: ToastMessage) => setToasts((list) => [...list, { ...message, id: Date.now() + Math.random() }]), []);
  return (
    <ToastContext.Provider value={notify}>
      <ToastPrimitive.Provider duration={duration} swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <ToastPrimitive.Root
            key={toast.id}
            className={`bb-toast bb-tone--${toast.tone ?? 'info'}`}
            type={toast.tone === 'danger' ? 'foreground' : 'background'}
            onOpenChange={(open) => !open && setToasts((list) => list.filter((t) => t.id !== toast.id))}
          >
            <ToastPrimitive.Title className="bb-toast__title">{toast.title}</ToastPrimitive.Title>
            {toast.description && <ToastPrimitive.Description>{toast.description}</ToastPrimitive.Description>}
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="bb-toast-viewport" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): (message: ToastMessage) => void {
  const notify = useContext(ToastContext);
  if (!notify) throw new Error('useToast must be used inside <ToastProvider>');
  return notify;
}
